<template>
  <div>
    <pt-modal
      ref="addEditItemModal"
      :close-on-outside-click="false"
      size="large"
      no-border
      class="custom-modal unavailability-modal"
    >
      <div slot="modal-header" class="flex justify-between text-xl">
        <pt-loader v-if="isBodyLoading" icon-class="fa-refresh fa-5x text-white" container="body" />
        <div class="">{{ isEdit ? 'Edit' : 'Add' }} Menu Item</div>
        <div>
          <button @click="close">
            <i class="fa fa-times" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div slot="modal-body">
        <pt-form-group :invalid-feedback="formErrors.invalid_name" class="mb-4">
          <div slot="form-group-label" class="text-black-darker text-sm font-bold mb-1">
            Item Name <span class="font-bold text-red">*</span>
          </div>
          <pt-form-input
            id="name"
            v-model="$v.itemName.$model"
            type="text"
            placeholder="Type Item Name"
            class="w-full"
          />
        </pt-form-group>
        <pt-form-group class="mb-4">
          <div slot="form-group-label" class="text-black-darker text-sm font-bold mb-1">
            Description
          </div>
          <pt-form-textarea v-model="description" :rows="5" placeholder="Type Item Description" class="w-full" />
        </pt-form-group>
        <div class="flex mb-4">
          <pt-form-group :invalid-feedback="formErrors.invalid_category" class="w-1/2 mr-2">
            <div slot="form-group-label" class="text-black-darker text-sm font-bold mb-1">
              Category Name <span class="font-bold text-red">*</span>
            </div>
            <pt-select
              v-model="$v.category.$model"
              :options="categories"
              :allow-empty="false"
              track-by="id"
              label-key="name"
              class="w-full"
            />
          </pt-form-group>
          <pt-form-group :invalid-feedback="formErrors.invalid_price" class="w-1/2 ml-2">
            <div slot="form-group-label" class="text-black-darker text-sm font-bold mb-1">
              Price <span class="font-bold text-red">*</span>
            </div>
            <pt-form-input
              id="name"
              v-model="$v.price.$model"
              type="text"
              placeholder="Type Item Price"
              class="w-full h-full input-full"
            />
          </pt-form-group>
        </div>
        <pt-form-group :invalid-feedback="formErrors.invalid_menu_hour" class="w-full mb-6">
          <div slot="form-group-label" class="text-black-darker text-sm font-bold mb-1">
            Menu Hours <span class="font-bold text-red">*</span>
          </div>
          <pt-select
            v-model="$v.menuHour.$model"
            :options="menuHours"
            :allow-empty="false"
            track-by="id"
            label-key="menu_type"
            class="w-full"
          />
        </pt-form-group>
        <pt-form-group :invalid-feedback="formErrors.itemImage">
          <div slot="form-group-label" class="text-black-darker text-sm font-bold mb-1">
            Item Image
          </div>
          <image-uploader
            :uploaded-image="getImageWithParam"
            image-id="itemImage"
            class="mb-2"
            @onImageUpload="handleItemImage"
            @onImageRemove="handleItemImageRemove"
          />
          <p class="text-grey text-sm mb-1">
            * Adding menu item is subject to Support Approval. Upon review of the item, Support will either approve your
            item or decline with proper reason. On both cases, you will be notified.
          </p>
          <p class="text-grey text-sm">
            ** Minimum item image resolution is 500x500 pixels.
          </p>
        </pt-form-group>
      </div>
      <div slot="modal-footer">
        <div class="flex w-full sm:flex-col md:flex-row md:justify-end">
          <pt-button
            :disabled="isLoading"
            class="sm:w-full md:w-auto md:px-10 my-1 mr-2"
            variant="secondary-outline"
            @action="close"
          >
            Cancel
          </pt-button>
          <pt-button :disabled="isLoading" class="sm:w-full md:w-auto md:px-10 my-1" variant="primary" @action="submit">
            {{ isEdit ? 'Update' : 'Add' }}
          </pt-button>
        </div>
      </div>
    </pt-modal>
  </div>
</template>

<script>
import apiRequest from '@src/utils/apiRequest';
import { PtModal, PtButton } from '@pathao/ui-components';
import { PtFormGroup, PtFormInput, PtFormTextarea } from '@pathao/ui-components/esm/FormComponents';
import { PtSelect } from '@pathao/ui-components/esm/Select';
import ImageUploader from '@components/common/ImageUploader.vue';
import { required, numeric } from 'vuelidate/lib/validators';
import { RESTO_GET_ITEM, RESTO_CREATE_ITEM, RESTO_UPDATE_ITEM } from '@src/config/apiEndpoints';

export default {
  components: {
    PtModal,
    PtButton,
    PtFormGroup,
    PtFormInput,
    PtFormTextarea,
    PtSelect,
    ImageUploader,
  },
  props: {
    itemId: {
      type: String,
      default: null,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    menuHours: {
      type: Array,
      default: () => [],
    },
    isEdit: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      isBodyLoading: false,
      itemName: null,
      description: null,
      category: null,
      price: null,
      menuHour: null,
      itemImage: null,
      item: {},
    };
  },
  validations: {
    itemName: { required },
    price: { required, numeric },
    menuHour: { required },
    category: { required },
  },
  computed: {
    formErrors() {
      const errors = {};

      const hasErrorInName = this.$v.itemName.$dirty && this.$v.itemName.$invalid;
      const hasErrorInPrice = this.$v.price.$dirty && this.$v.price.$invalid;
      const hasErrorInMenuHour = this.$v.menuHour.$dirty && this.$v.menuHour.$invalid;
      const hasErrorInCategory = this.$v.category.$dirty && this.$v.category.$invalid;

      if (hasErrorInName) {
        errors.invalid_name = 'Please provide a Item name';
      }

      if (hasErrorInPrice) {
        errors.invalid_price = 'Please provide valid Item price';
      }

      if (hasErrorInMenuHour) {
        errors.invalid_menu_hour = 'Please select a menu hour';
      }

      if (hasErrorInCategory) {
        errors.invalid_category = 'Please select a category';
      }

      return errors;
    },
    getImageWithParam() {
      return this.itemImage ? `${this.itemImage}?${this.timestamp}` : null;
    },
    timestamp() {
      // Generate the timestamp only when itemImage changes
      return this.itemImage ? performance.now() : null;
    },
  },
  watch: {
    async itemId(newVal, oldVal) {
      if (this.isEdit && newVal) {
        this.isBodyLoading = true;
        try {
          const payload = {
            url: RESTO_GET_ITEM.replace(':itemId', newVal),
            method: 'GET',
          };
          apiRequest(payload).then(response => {
            this.isBodyLoading = false;
            this.item = response.data.data;
            this.populateFormData(response.data.data);
          });
        } catch (error) {
          this.isBodyLoading = false;
          this.$notification.error('Failed to get Item Info. Please Try Again!');
          this.close();
        }
      }
    },
    async info() {
      if (this.info) {
        this.isLoading = true;
        try {
          if (this.info.group_id) {
            await this.getGroupDetails(this.info.group_id);
          } else {
            this.resetGroupDetails(null);
          }
          await this.populateFormData(this.info);
          this.isChainResto = this.info.is_resto;
          this.isSubsidy = this.info.subsidy_pathao_share + this.info.subsidy_resto_share > 0;
        } catch (error) {
        } finally {
          this.isLoading = false;
        }
      }
    },
  },
  // mounted() {
  //   if (this.isEdit) {
  //     this.populateFormData(this.item);
  //   }
  // },
  methods: {
    handleItemImage(event, image) {
      this.itemImage = image;
    },
    handleItemImageRemove() {
      this.itemImage = null;
    },
    populateFormData(item) {
      this.itemName = item.name;
      this.description = item.description;
      this.category = item.category;
      this.price = item.price;
      this.itemImage = item.image;
      this.menuHour = item.menu_hour;
    },
    show() {
      if (this.isEdit) {
        this.populateFormData(this.item);
      } else {
        this.resetData();
      }
      this.$refs.addEditItemModal.showModal();
    },
    close() {
      // this.$emit('onUnavailability', !this.isSubmitClicked ? false : this.selectedValue);
      if (this.isLoading) return;
      if (!this.isEdit) {
        this.resetData();
      }
      this.$refs.addEditItemModal.closeModal();
    },
    resetData() {
      this.itemName = null;
      this.description = null;
      this.category = null;
      this.price = null;
      this.itemImage = null;
      this.menuHour = null;
      this.item = null;
    },
    submit() {
      this.isLoading = true;

      this.$v.itemName.$touch();
      this.$v.price.$touch();

      if (this.$v.$invalid) {
        this.isLoading = false;
        this.$notification.error('Please fill up all required fields!');
        return;
      }

      let formData = new FormData();

      if (!this.isEdit) {
        formData.append('name', this.itemName);
        formData.append('price', this.price);
        formData.append('category_id', this.category.id);
        formData.append('menu_hour_id', this.menuHour.id);
        if (this.itemImage) formData.append('image', this.itemImage);
        if (this.description) formData.append('description', this.description);
      }

      if (this.isEdit && this.item) {
        formData = new FormData();
        if (this.item.name !== this.itemName) formData.append('name', this.itemName);
        if (this.item.price !== this.price) formData.append('price', this.price);
        if (this.item.category.id !== this.category.id) formData.append('category_id', this.category.id);
        if (this.item.menu_hour.id !== this.menuHour.id) formData.append('menu_hour_id', this.menuHour.id);
        if (this.item.image !== this.itemImage) formData.append('image', this.itemImage);
        if (this.item.description !== this.description) formData.append('description', this.description);
      }

      let payload = {
        method: this.isEdit ? 'PATCH' : 'PUT',
        url: this.isEdit ? RESTO_UPDATE_ITEM.replace(':itemId', this.item.id) : RESTO_CREATE_ITEM,
        data: formData,
      };

      apiRequest(payload)
        .then(response => {
          this.$notification.success(response.data.message);
          this.isLoading = false;
          this.close();
        })
        .catch(() => {
          this.isLoading = false;
          this.$notification.error(`Failed to ${this.isEdit ? 'update' : 'create'} item`);
        });
    },
  },
};
</script>
